import { Button, Input, InputNumber, Select, Space } from '@/shared/ui/primitives';
import { DeleteOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import EntityFilterSelect from '@/shared/components/EntityFilterSelect';
import type { DynamicFilterCondition, DynamicRelationLookups, FilterFieldMetadata, FilterOperator } from './types';

type Props = {
  fields: FilterFieldMetadata[];
  condition: DynamicFilterCondition;
  relationLookups?: DynamicRelationLookups;
  onChange: (condition: DynamicFilterCondition) => void;
  onRemove: () => void;
};

const isMultiOperator = (operator: FilterOperator) => operator === 'IN' || operator === 'NOT_IN';
const isBetweenOperator = (operator: FilterOperator) => operator === 'BETWEEN';

export default function DynamicFilterRow({ fields, condition, relationLookups, onChange, onRemove }: Props) {
  const { t } = useTranslation();
  const field = fields.find((item) => item.key === condition.field) ?? fields[0];
  const operator = field.operators.includes(condition.operator) ? condition.operator : field.operators[0];

  const setField = (key: string) => {
    const nextField = fields.find((item) => item.key === key);
    if (!nextField) return;
    onChange({ field: nextField.key, operator: nextField.operators[0], value: emptyValueFor(nextField, nextField.operators[0]) });
  };

  const setOperator = (nextOperator: FilterOperator) => {
    onChange({ ...condition, operator: nextOperator, value: emptyValueFor(field, nextOperator) });
  };

  return (
    <Space style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Select
        value={field.key}
        onChange={setField}
        options={fields.map((item) => ({ value: item.key, label: fieldLabel(item, t) }))}
        style={{ minWidth: 190 }}
        showSearch
      />
      <Select
        value={operator}
        onChange={(value) => setOperator(value as FilterOperator)}
        options={field.operators.map((item) => ({ value: item, label: operatorLabel(item, t) }))}
        style={{ minWidth: 150 }}
      />
      {renderValueInput(field, { ...condition, operator }, relationLookups, t, (value) => onChange({ ...condition, operator, value }))}
      <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove} />
    </Space>
  );
}

function renderValueInput(
  field: FilterFieldMetadata,
  condition: DynamicFilterCondition,
  relationLookups: DynamicRelationLookups | undefined,
  t: (key: string) => string,
  onValueChange: (value: unknown) => void,
) {
  const relationLookup = relationLookups?.[field.key];
  if (relationLookup && condition.operator === 'EQUALS') {
    return (
      <EntityFilterSelect
        label=""
        value={(condition.value as any) || ''}
        onChange={onValueChange as any}
        queryKey={['dynamic-filter-relation', field.key]}
        fetchFn={relationLookup.fetchFn}
        fetchById={relationLookup.fetchById}
        mapOption={relationLookup.mapOption}
        placeholder={relationLookup.placeholder ?? field.label}
      />
    );
  }

  if (field.type === 'ENUM') {
    return (
      <Select
        mode={isMultiOperator(condition.operator) ? 'multiple' : undefined}
        value={condition.value || (isMultiOperator(condition.operator) ? [] : undefined)}
        onChange={(value) => onValueChange(value ?? (isMultiOperator(condition.operator) ? [] : ''))}
        options={field.options ?? []}
        style={{ minWidth: 180 }}
        allowClear
      />
    );
  }

  if (field.type === 'BOOLEAN') {
    return (
      <Select
        value={condition.value === true ? 'true' : condition.value === false ? 'false' : undefined}
        onChange={(value) => onValueChange(value === 'true')}
        options={[
          { value: 'true', label: t('common.yes') },
          { value: 'false', label: t('common.no') },
        ]}
        style={{ minWidth: 120 }}
        allowClear
      />
    );
  }

  if (field.type === 'NUMBER') {
    if (isBetweenOperator(condition.operator)) {
      const range = isRangeObject(condition.value) ? condition.value : { from: undefined, to: undefined };
      return (
        <Space.Compact>
          <InputNumber
            value={range.from !== undefined && range.from !== '' ? Number(range.from) : undefined}
            onChange={(value) => onValueChange({ ...range, from: value ?? '' })}
            placeholder={t('common.from')}
            style={{ width: 105 }}
          />
          <InputNumber
            value={range.to !== undefined && range.to !== '' ? Number(range.to) : undefined}
            onChange={(value) => onValueChange({ ...range, to: value ?? '' })}
            placeholder={t('common.to')}
            style={{ width: 105 }}
          />
        </Space.Compact>
      );
    }
    return (
      <InputNumber
        value={condition.value !== undefined && condition.value !== '' ? Number(condition.value) : undefined}
        onChange={(value) => onValueChange(value ?? '')}
        style={{ width: 180 }}
      />
    );
  }

  if (field.type === 'DATE' || field.type === 'DATETIME') {
    const showTime = field.type === 'DATETIME';
    if (isBetweenOperator(condition.operator)) {
      const range = isRangeObject(condition.value) ? condition.value : { from: '', to: '' };
      return (
        <Space.Compact>
          <Input
            type={showTime ? 'datetime-local' : 'date'}
            value={toDateInputValue(range.from, showTime)}
            onChange={(event: any) => onValueChange({ ...range, from: fromDateInputValue(event.target.value, showTime, false) })}
            placeholder={t('common.from')}
            style={{ width: showTime ? 190 : 140 }}
          />
          <Input
            type={showTime ? 'datetime-local' : 'date'}
            value={toDateInputValue(range.to, showTime)}
            onChange={(event: any) => onValueChange({ ...range, to: fromDateInputValue(event.target.value, showTime, true) })}
            placeholder={t('common.to')}
            style={{ width: showTime ? 190 : 140 }}
          />
        </Space.Compact>
      );
    }
    if (condition.operator === 'LAST_DAYS') {
      return (
        <InputNumber
          value={condition.value !== undefined && condition.value !== '' ? Number(condition.value) : undefined}
          onChange={(value) => onValueChange(value ?? '')}
          placeholder={t('dynamicFilters.days')}
          style={{ width: 140 }}
        />
      );
    }
    return (
      <Input
        type={showTime ? 'datetime-local' : 'date'}
        value={toDateInputValue(condition.value, showTime)}
        onChange={(event: any) => onValueChange(fromDateInputValue(event.target.value, showTime, condition.operator === 'BEFORE'))}
        style={{ width: showTime ? 190 : 150 }}
      />
    );
  }

  return (
    <Input
      value={typeof condition.value === 'string' ? condition.value : ''}
      onChange={(event: any) => onValueChange(event.target.value)}
      style={{ width: 220 }}
    />
  );
}

function emptyValueFor(field: FilterFieldMetadata, operator: FilterOperator) {
  if (isBetweenOperator(operator)) return { from: '', to: '' };
  if (isMultiOperator(operator)) return [];
  if (field.type === 'BOOLEAN') return undefined;
  return '';
}

function isRangeObject(value: unknown): value is { from?: unknown; to?: unknown } {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function toDateInputValue(value: unknown, showTime: boolean) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  if (!showTime) return `${year}-${month}-${day}`;
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function fromDateInputValue(value: string, showTime: boolean, endOfDay: boolean) {
  if (!value) return '';
  const date = showTime ? new Date(value) : dateFromLocalDateInput(value);
  if (Number.isNaN(date.getTime())) return '';
  if (!showTime) {
    if (endOfDay) date.setHours(23, 59, 59, 999);
    else date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
}

function dateFromLocalDateInput(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function operatorLabel(operator: string, t: (key: string, options?: any) => string) {
  return t(`dynamicFilters.operators.${operator}`, {
    defaultValue: operator
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' '),
  });
}

function fieldLabel(field: FilterFieldMetadata, t: (key: string, options?: any) => string) {
  const normalized = field.key.replace(/\.(id|geoCityId|geoDistrictId)$/i, (_, suffix) => {
    if (suffix === 'geoCityId') return 'CityId';
    if (suffix === 'geoDistrictId') return 'DistrictId';
    return 'Id';
  });
  return t(`filters.${field.key}`, {
    defaultValue: t(`filters.${normalized}`, { defaultValue: field.label }),
  });
}
